import React from 'react';
import FeatherIcon from '../FeatherIcon';
import { EDITIONS, FOUNDERS_THEME, estimatedContributionCents, formatPrice, type EditionId } from '../../products/editions';
import { GoldLogo, ProductImageFrame, StatusChip, scrollToSection } from './shared';

/** Four summary cards. An offered edition links to its listing and, through Buy soon, to the vote. */
const EditionCards: React.FC<{ onBuySoon: (id: EditionId) => void }> = ({ onBuySoon }) => (
  <section id="editions" aria-labelledby="editions-heading" className="py-16">
    <div className="container max-w-7xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 id="editions-heading" className="text-3xl font-bold mb-4 text-gray-800">Four Proposed Editions</h2>
        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
          Every edition is a full, playable custom deck. None is available to buy yet.
        </p>
      </div>

      <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {EDITIONS.map((edition) => {
          const founders = edition.theme === FOUNDERS_THEME;
          if (edition.comingSoon) {
            return (
              <li key={edition.id} className="card flex flex-col overflow-hidden opacity-75">
                <ProductImageFrame image={edition.detailImage} label={edition.name} theme={edition.theme} className="rounded-none" />
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="text-xl font-bold text-gray-800">{edition.name}</h3>
                  <p className="mt-1 text-3xl font-bold text-gray-500">{formatPrice(edition.priceCents)}</p>
                  <p className="mt-4 text-sm text-gray-600">{edition.description}</p>
                  <div className="mt-auto pt-6">
                    <StatusChip className="border-gray-300 text-gray-600">Coming soon</StatusChip>
                  </div>
                </div>
              </li>
            );
          }
          return (
            <li
              key={edition.id}
              className={`card flex flex-col overflow-hidden ${founders ? 'border-[#d4af37]/70 hover:border-[#d4af37]' : ''}`}
            >
              <ProductImageFrame
                image={edition.detailImage}
                label={edition.name}
                theme={edition.theme}
                gold={founders}
                className="rounded-none"
              />
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-xl font-bold text-gray-800">{edition.name}</h3>
                  {founders && <GoldLogo className="h-7 w-7 shrink-0 rounded-full bg-black p-0.5" />}
                </div>
                <p className="mt-1 text-3xl font-bold text-gray-900">{formatPrice(edition.priceCents)}</p>
                <p className="mt-1 text-sm text-gray-600">
                  Est. voucher contribution{' '}
                  <span className="font-semibold text-secondary">{formatPrice(estimatedContributionCents(edition))}</span>
                </p>
                <p className="mt-0.5 text-[10px] leading-snug text-gray-500">
                  Assumed manufacturing cost {formatPrice(edition.estimatedManufacturingCostCents)}; real cost will vary.
                </p>
                <p className="mt-4 text-sm text-gray-700">{edition.description}</p>
                <ul className="mt-4 space-y-2 text-sm text-gray-700">
                  {edition.highlights.map((highlight) => (
                    <li key={highlight} className="flex gap-2">
                      <FeatherIcon name="check" size={16} className={`mt-0.5 shrink-0 ${founders ? 'text-tertiary' : 'text-secondary'}`} />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6 space-y-4">
                  <StatusChip className="border-gray-300 text-gray-600">
                    Prototype · Pre-production
                    {edition.productionLimit && ` · Limited to ${edition.productionLimit}`}
                  </StatusChip>
                  <button
                    type="button"
                    onClick={() => onBuySoon(edition.id)}
                    className={`w-full py-2.5 ${founders ? FOUNDERS_THEME.button : 'btn-primary'}`}
                    aria-label={`Buy soon: ${edition.name}. Tell us you would buy it.`}
                  >
                    Buy soon
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollToSection(`edition-${edition.id}`)}
                    className="w-full text-sm font-medium text-primary hover:underline"
                    aria-label={`View full details: ${edition.name}`}
                  >
                    View full details
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  </section>
);

export default EditionCards;
